import React, { createContext, useContext, useState, useCallback } from 'react';
import { Audit, AuditRequest, AuditStatus } from '@/lib/types';
import { mockAudits } from '@/lib/mockData';

interface AuditContextType {
  audits: Audit[];
  getAuditsByClient: (clientId: string) => Audit[];
  getAuditById: (id: string) => Audit | undefined;
  createAuditRequest: (clientId: string, clientName: string, request: AuditRequest) => Promise<Audit>;
  updateAudit: (id: string, updates: Partial<Audit>) => Promise<Audit>;
}

const AuditContext = createContext<AuditContextType | undefined>(undefined);

export function AuditProvider({ children }: { children: React.ReactNode }) {
  const [audits, setAudits] = useState<Audit[]>(mockAudits);

  const getAuditsByClient = useCallback((clientId: string) => {
    return audits.filter(a => a.clientId === clientId);
  }, [audits]);

  const getAuditById = useCallback((id: string) => {
    return audits.find(a => a.id === id);
  }, [audits]);

  const createAuditRequest = useCallback(async (clientId: string, clientName: string, request: AuditRequest): Promise<Audit> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newAudit: Audit = {
      id: Date.now().toString(),
      clientId,
      clientName,
      buildingType: request.buildingType,
      numberOfElevators: request.numberOfElevators,
      yearOfInstallation: request.yearOfInstallation,
      lastInspectionDate: request.lastInspectionDate,
      mainConcern: request.mainConcern,
      documentUrl: request.documentUrl,
      planId: request.planId,
      technicalScore: 0,
      status: 'in_analysis',
      safety: { explanation: '', observations: [] },
      compliance: { explanation: '', observations: [] },
      futureRisk: { explanation: '', observations: [] },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    setAudits(prev => [...prev, newAudit]);
    return newAudit;
  }, []);

  const updateAudit = useCallback(async (id: string, updates: Partial<Audit>): Promise<Audit> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let updatedAudit: Audit | undefined;
    
    setAudits(prev => prev.map(audit => {
      if (audit.id === id) {
        updatedAudit = { ...audit, ...updates, updatedAt: new Date().toISOString() };
        return updatedAudit;
      }
      return audit;
    }));
    
    if (!updatedAudit) {
      throw new Error('Auditoria não encontrada');
    }
    
    return updatedAudit;
  }, []);

  return (
    <AuditContext.Provider value={{ audits, getAuditsByClient, getAuditById, createAuditRequest, updateAudit }}>
      {children}
    </AuditContext.Provider>
  );
}

export function useAudit() {
  const context = useContext(AuditContext);
  if (context === undefined) {
    throw new Error('useAudit must be used within an AuditProvider');
  }
  return context;
}
